import { useTranslation } from 'react-i18next';
import { useState, memo } from 'react';
import {
  Wifi,
  Car,
  Snowflake,
  Tv,
  UtensilsCrossed,
  WashingMachine,
  Wind,
  Coffee,
  Bath,
  Dumbbell,
  Waves,
  TreePine,
  Sparkles,
  ShowerHead,
  Refrigerator,
  Microwave,
  Home,
  BedDouble,
  ChefHat,
  Flame,
  Utensils,
  CookingPot,
  Sandwich,
  IceCream,
  Wine,
  Heater,
  Fan,
  Gamepad2,
  Music,
  Speaker,
  Radio,
  Monitor,
  Laptop,
  Printer,
  Armchair,
  BookOpen,
  Baby,
  Dog,
  Cat,
  Lock,
  ShieldCheck,
  Mountain,
  Sunrise,
  TreeDeciduous,
  Umbrella,
  Sailboat,
  Bike,
  Shirt,
  SprayCan,
  Zap,
  Droplets,
  Thermometer,
  Key,
  DoorOpen,
  ParkingCircle,
  CarFront,
  Bed,
  Moon,
  Sun,
  Accessibility,
  Eye,
  Hand,
  Heart,
  Plus,
  ChevronDown,
  ChevronUp,
  Sparkle,
  Cigarette,
} from 'lucide-react';

interface AmenitiesCardProps {
  amenities: string[];
  darkMode: boolean;
  compact?: boolean;
  /** Guest-visible note per amenity id (already translated). */
  notes?: Record<string, string>;
  /**
   * Amenity ids to hide because a dedicated detailed section already shows them
   * (TV/AC/kitchen/laundry have their own GuestEquipmentSection/KitchenSection),
   * avoiding showing the same thing twice. Default: show all.
   */
  hideAmenities?: string[];
}

// Kategorije sadržaja
type CategoryKey = 'basic' | 'climate' | 'entertainment' | 'kitchen' | 'bathroom' | 'laundry' | 'outdoor' | 'sports' | 'beach' | 'work' | 'sleeping' | 'family' | 'pets' | 'safety' | 'accessibility' | 'views' | 'services' | 'custom';

interface CategoryConfig {
  icon: React.ElementType;
  labelHr: string;
  labelEn: string;
  colorClass: string;
  darkColorClass: string;
  bgClass: string;
  darkBgClass: string;
}

const categoryConfig: Record<CategoryKey, CategoryConfig> = {
  basic: {
    icon: Home,
    labelHr: 'Osnovno',
    labelEn: 'Basic',
    colorClass: 'text-blue-600',
    darkColorClass: 'text-blue-400',
    bgClass: 'bg-blue-100',
    darkBgClass: 'bg-blue-500/20',
  },
  climate: {
    icon: Thermometer,
    labelHr: 'Klima i grijanje',
    labelEn: 'Climate',
    colorClass: 'text-cyan-600',
    darkColorClass: 'text-cyan-400',
    bgClass: 'bg-cyan-100',
    darkBgClass: 'bg-cyan-500/20',
  },
  entertainment: {
    icon: Tv,
    labelHr: 'Zabava',
    labelEn: 'Entertainment',
    colorClass: 'text-purple-600',
    darkColorClass: 'text-purple-400',
    bgClass: 'bg-purple-100',
    darkBgClass: 'bg-purple-500/20',
  },
  kitchen: {
    icon: ChefHat,
    labelHr: 'Kuhinja',
    labelEn: 'Kitchen',
    colorClass: 'text-orange-600',
    darkColorClass: 'text-orange-400',
    bgClass: 'bg-orange-100',
    darkBgClass: 'bg-orange-500/20',
  },
  bathroom: {
    icon: Bath,
    labelHr: 'Kupaonica',
    labelEn: 'Bathroom',
    colorClass: 'text-teal-600',
    darkColorClass: 'text-teal-400',
    bgClass: 'bg-teal-100',
    darkBgClass: 'bg-teal-500/20',
  },
  laundry: {
    icon: WashingMachine,
    labelHr: 'Rublje',
    labelEn: 'Laundry',
    colorClass: 'text-indigo-600',
    darkColorClass: 'text-indigo-400',
    bgClass: 'bg-indigo-100',
    darkBgClass: 'bg-indigo-500/20',
  },
  outdoor: {
    icon: TreePine,
    labelHr: 'Vanjski prostor',
    labelEn: 'Outdoor',
    colorClass: 'text-green-600',
    darkColorClass: 'text-green-400',
    bgClass: 'bg-green-100',
    darkBgClass: 'bg-green-500/20',
  },
  sports: {
    icon: Dumbbell,
    labelHr: 'Sport i fitness',
    labelEn: 'Sports & Fitness',
    colorClass: 'text-red-600',
    darkColorClass: 'text-red-400',
    bgClass: 'bg-red-100',
    darkBgClass: 'bg-red-500/20',
  },
  beach: {
    icon: Umbrella,
    labelHr: 'Plaža i more',
    labelEn: 'Beach & Sea',
    colorClass: 'text-sky-600',
    darkColorClass: 'text-sky-400',
    bgClass: 'bg-sky-100',
    darkBgClass: 'bg-sky-500/20',
  },
  work: {
    icon: Laptop,
    labelHr: 'Rad',
    labelEn: 'Work',
    colorClass: 'text-slate-600',
    darkColorClass: 'text-slate-400',
    bgClass: 'bg-slate-100',
    darkBgClass: 'bg-slate-500/20',
  },
  sleeping: {
    icon: Bed,
    labelHr: 'Spavanje',
    labelEn: 'Sleeping',
    colorClass: 'text-violet-600',
    darkColorClass: 'text-violet-400',
    bgClass: 'bg-violet-100',
    darkBgClass: 'bg-violet-500/20',
  },
  family: {
    icon: Baby,
    labelHr: 'Obitelj',
    labelEn: 'Family',
    colorClass: 'text-pink-600',
    darkColorClass: 'text-pink-400',
    bgClass: 'bg-pink-100',
    darkBgClass: 'bg-pink-500/20',
  },
  pets: {
    icon: Dog,
    labelHr: 'Kućni ljubimci',
    labelEn: 'Pets',
    colorClass: 'text-amber-600',
    darkColorClass: 'text-amber-400',
    bgClass: 'bg-amber-100',
    darkBgClass: 'bg-amber-500/20',
  },
  safety: {
    icon: ShieldCheck,
    labelHr: 'Sigurnost',
    labelEn: 'Safety',
    colorClass: 'text-emerald-600',
    darkColorClass: 'text-emerald-400',
    bgClass: 'bg-emerald-100',
    darkBgClass: 'bg-emerald-500/20',
  },
  accessibility: {
    icon: Accessibility,
    labelHr: 'Pristupačnost',
    labelEn: 'Accessibility',
    colorClass: 'text-blue-600',
    darkColorClass: 'text-blue-400',
    bgClass: 'bg-blue-100',
    darkBgClass: 'bg-blue-500/20',
  },
  views: {
    icon: Mountain,
    labelHr: 'Pogled',
    labelEn: 'Views',
    colorClass: 'text-lime-600',
    darkColorClass: 'text-lime-400',
    bgClass: 'bg-lime-100',
    darkBgClass: 'bg-lime-500/20',
  },
  services: {
    icon: Heart,
    labelHr: 'Usluge',
    labelEn: 'Services',
    colorClass: 'text-rose-600',
    darkColorClass: 'text-rose-400',
    bgClass: 'bg-rose-100',
    darkBgClass: 'bg-rose-500/20',
  },
  custom: {
    icon: Sparkle,
    labelHr: 'Ostalo',
    labelEn: 'Other',
    colorClass: 'text-gray-600',
    darkColorClass: 'text-gray-400',
    bgClass: 'bg-gray-100',
    darkBgClass: 'bg-gray-500/20',
  },
};

// Mapiranje amenitija na ikone, prijevode i kategorije
const amenityConfig: { [key: string]: { icon: React.ElementType; labelHr: string; labelEn: string; category: CategoryKey } } = {
  // Basic
  wifi: { icon: Wifi, labelHr: 'WiFi', labelEn: 'WiFi', category: 'basic' },
  parking: { icon: Car, labelHr: 'Parking', labelEn: 'Parking', category: 'basic' },
  free_parking: { icon: ParkingCircle, labelHr: 'Besplatni parking', labelEn: 'Free Parking', category: 'basic' },
  elevator: { icon: DoorOpen, labelHr: 'Dizalo', labelEn: 'Elevator', category: 'basic' },
  doorman: { icon: Key, labelHr: 'Portir', labelEn: 'Doorman', category: 'basic' },

  // Climate
  aircon: { icon: Snowflake, labelHr: 'Klima', labelEn: 'Air Conditioning', category: 'climate' },
  ac: { icon: Snowflake, labelHr: 'Klima uređaj', labelEn: 'Air Conditioning', category: 'climate' },
  ac_bedroom: { icon: Snowflake, labelHr: 'Klima u spavaćoj sobi', labelEn: 'Bedroom AC', category: 'climate' },
  air_conditioning: { icon: Snowflake, labelHr: 'Klima', labelEn: 'Air Conditioning', category: 'climate' },
  heating: { icon: Heater, labelHr: 'Grijanje', labelEn: 'Heating', category: 'climate' },
  central_heating: { icon: Heater, labelHr: 'Centralno grijanje', labelEn: 'Central Heating', category: 'climate' },
  heating_central: { icon: Heater, labelHr: 'Centralno grijanje', labelEn: 'Central Heating', category: 'climate' },
  heating_floor: { icon: Heater, labelHr: 'Podno grijanje', labelEn: 'Floor Heating', category: 'climate' },
  fireplace: { icon: Flame, labelHr: 'Kamin', labelEn: 'Fireplace', category: 'climate' },
  fan: { icon: Fan, labelHr: 'Ventilator', labelEn: 'Fan', category: 'climate' },
  ceiling_fan: { icon: Fan, labelHr: 'Stropni ventilator', labelEn: 'Ceiling Fan', category: 'climate' },
  fan_ceiling: { icon: Fan, labelHr: 'Stropni ventilator', labelEn: 'Ceiling Fan', category: 'climate' },
  humidifier: { icon: Droplets, labelHr: 'Ovlaživač zraka', labelEn: 'Humidifier', category: 'climate' },
  dehumidifier: { icon: Droplets, labelHr: 'Odvlaživač zraka', labelEn: 'Dehumidifier', category: 'climate' },
  air_purifier: { icon: Wind, labelHr: 'Pročišćivač zraka', labelEn: 'Air Purifier', category: 'climate' },

  // Entertainment
  tv: { icon: Tv, labelHr: 'TV', labelEn: 'TV', category: 'entertainment' },
  smart_tv: { icon: Monitor, labelHr: 'Smart TV', labelEn: 'Smart TV', category: 'entertainment' },
  cable_tv: { icon: Tv, labelHr: 'Kabelska TV', labelEn: 'Cable TV', category: 'entertainment' },
  satellite_tv: { icon: Tv, labelHr: 'Satelitska TV', labelEn: 'Satellite TV', category: 'entertainment' },
  netflix: { icon: Monitor, labelHr: 'Netflix', labelEn: 'Netflix', category: 'entertainment' },
  streaming: { icon: Monitor, labelHr: 'Streaming', labelEn: 'Streaming Services', category: 'entertainment' },
  playstation: { icon: Gamepad2, labelHr: 'PlayStation', labelEn: 'PlayStation', category: 'entertainment' },
  xbox: { icon: Gamepad2, labelHr: 'Xbox', labelEn: 'Xbox', category: 'entertainment' },
  nintendo_switch: { icon: Gamepad2, labelHr: 'Nintendo Switch', labelEn: 'Nintendo Switch', category: 'entertainment' },
  game_console: { icon: Gamepad2, labelHr: 'Igraća konzola', labelEn: 'Game Console', category: 'entertainment' },
  board_games: { icon: Gamepad2, labelHr: 'Društvene igre', labelEn: 'Board Games', category: 'entertainment' },
  books: { icon: BookOpen, labelHr: 'Knjige', labelEn: 'Books', category: 'entertainment' },
  sound_system: { icon: Speaker, labelHr: 'Zvučni sustav', labelEn: 'Sound System', category: 'entertainment' },
  bluetooth_speaker: { icon: Speaker, labelHr: 'Bluetooth zvučnik', labelEn: 'Bluetooth Speaker', category: 'entertainment' },
  radio: { icon: Radio, labelHr: 'Radio', labelEn: 'Radio', category: 'entertainment' },
  piano: { icon: Music, labelHr: 'Klavir', labelEn: 'Piano', category: 'entertainment' },
  guitar: { icon: Music, labelHr: 'Gitara', labelEn: 'Guitar', category: 'entertainment' },
  karaoke: { icon: Music, labelHr: 'Karaoke', labelEn: 'Karaoke', category: 'entertainment' },
  projector: { icon: Monitor, labelHr: 'Projektor', labelEn: 'Projector', category: 'entertainment' },
  home_cinema: { icon: Monitor, labelHr: 'Kućno kino', labelEn: 'Home Cinema', category: 'entertainment' },
  billiards: { icon: Gamepad2, labelHr: 'Bilijar', labelEn: 'Pool Table', category: 'entertainment' },
  table_tennis: { icon: Gamepad2, labelHr: 'Stolni tenis', labelEn: 'Table Tennis', category: 'entertainment' },
  foosball: { icon: Gamepad2, labelHr: 'Stolni nogomet', labelEn: 'Foosball', category: 'entertainment' },
  darts: { icon: Gamepad2, labelHr: 'Pikado', labelEn: 'Darts', category: 'entertainment' },

  // Kitchen
  kitchen: { icon: UtensilsCrossed, labelHr: 'Kuhinja', labelEn: 'Kitchen', category: 'kitchen' },
  kitchen_full: { icon: UtensilsCrossed, labelHr: 'Potpuno opremljena kuhinja', labelEn: 'Fully Equipped Kitchen', category: 'kitchen' },
  kitchen_basic: { icon: UtensilsCrossed, labelHr: 'Osnovna kuhinja', labelEn: 'Basic Kitchen', category: 'kitchen' },
  kitchenette: { icon: UtensilsCrossed, labelHr: 'Čajna kuhinja', labelEn: 'Kitchenette', category: 'kitchen' },
  outdoor_kitchen: { icon: UtensilsCrossed, labelHr: 'Vanjska kuhinja', labelEn: 'Outdoor Kitchen', category: 'kitchen' },
  fridge: { icon: Refrigerator, labelHr: 'Hladnjak', labelEn: 'Refrigerator', category: 'kitchen' },
  fridge_large: { icon: Refrigerator, labelHr: 'Veliki hladnjak', labelEn: 'Large Refrigerator', category: 'kitchen' },
  refrigerator: { icon: Refrigerator, labelHr: 'Hladnjak', labelEn: 'Refrigerator', category: 'kitchen' },
  freezer: { icon: Refrigerator, labelHr: 'Zamrzivač', labelEn: 'Freezer', category: 'kitchen' },
  microwave: { icon: Microwave, labelHr: 'Mikrovalna', labelEn: 'Microwave', category: 'kitchen' },
  oven: { icon: CookingPot, labelHr: 'Pećnica', labelEn: 'Oven', category: 'kitchen' },
  stove: { icon: Flame, labelHr: 'Štednjak', labelEn: 'Stove', category: 'kitchen' },
  stove_gas: { icon: Flame, labelHr: 'Plinski štednjak', labelEn: 'Gas Stove', category: 'kitchen' },
  stove_induction: { icon: Zap, labelHr: 'Indukcijska ploča', labelEn: 'Induction Cooktop', category: 'kitchen' },
  induction: { icon: Zap, labelHr: 'Indukcijska ploča', labelEn: 'Induction Cooktop', category: 'kitchen' },
  dishwasher: { icon: Droplets, labelHr: 'Perilica posuđa', labelEn: 'Dishwasher', category: 'kitchen' },
  coffee: { icon: Coffee, labelHr: 'Aparat za kavu', labelEn: 'Coffee Maker', category: 'kitchen' },
  coffee_maker: { icon: Coffee, labelHr: 'Aparat za kavu', labelEn: 'Coffee Maker', category: 'kitchen' },
  coffee_machine: { icon: Coffee, labelHr: 'Aparat za kavu', labelEn: 'Coffee Machine', category: 'kitchen' },
  coffee_nespresso: { icon: Coffee, labelHr: 'Nespresso aparat', labelEn: 'Nespresso', category: 'kitchen' },
  coffee_filter: { icon: Coffee, labelHr: 'Filter kava', labelEn: 'Filter Coffee', category: 'kitchen' },
  coffee_espresso: { icon: Coffee, labelHr: 'Espresso aparat', labelEn: 'Espresso Machine', category: 'kitchen' },
  espresso_machine: { icon: Coffee, labelHr: 'Espresso aparat', labelEn: 'Espresso Machine', category: 'kitchen' },
  french_press: { icon: Coffee, labelHr: 'French press', labelEn: 'French Press', category: 'kitchen' },
  nespresso: { icon: Coffee, labelHr: 'Nespresso', labelEn: 'Nespresso', category: 'kitchen' },
  kettle: { icon: Coffee, labelHr: 'Kuhalo za vodu', labelEn: 'Electric Kettle', category: 'kitchen' },
  toaster: { icon: Sandwich, labelHr: 'Toster', labelEn: 'Toaster', category: 'kitchen' },
  blender: { icon: CookingPot, labelHr: 'Blender', labelEn: 'Blender', category: 'kitchen' },
  mixer: { icon: CookingPot, labelHr: 'Mikser', labelEn: 'Mixer', category: 'kitchen' },
  food_processor: { icon: CookingPot, labelHr: 'Multipraktik', labelEn: 'Food Processor', category: 'kitchen' },
  air_fryer: { icon: CookingPot, labelHr: 'Air fryer', labelEn: 'Air Fryer', category: 'kitchen' },
  slow_cooker: { icon: CookingPot, labelHr: 'Slow cooker', labelEn: 'Slow Cooker', category: 'kitchen' },
  rice_cooker: { icon: CookingPot, labelHr: 'Kuhalo za rižu', labelEn: 'Rice Cooker', category: 'kitchen' },
  waffle_maker: { icon: CookingPot, labelHr: 'Aparat za vafle', labelEn: 'Waffle Maker', category: 'kitchen' },
  juicer: { icon: CookingPot, labelHr: 'Sokovnik', labelEn: 'Juicer', category: 'kitchen' },
  cooking_basics: { icon: Utensils, labelHr: 'Pribor za kuhanje', labelEn: 'Cooking Basics', category: 'kitchen' },
  dishes: { icon: Utensils, labelHr: 'Posuđe', labelEn: 'Dishes & Cutlery', category: 'kitchen' },
  wine_glasses: { icon: Wine, labelHr: 'Čaše za vino', labelEn: 'Wine Glasses', category: 'kitchen' },
  wine_cooler: { icon: Wine, labelHr: 'Hladnjak za vino', labelEn: 'Wine Cooler', category: 'kitchen' },
  minibar: { icon: Wine, labelHr: 'Minibar', labelEn: 'Minibar', category: 'kitchen' },
  water_filter: { icon: Droplets, labelHr: 'Filter za vodu', labelEn: 'Water Filter', category: 'kitchen' },
  bbq: { icon: Flame, labelHr: 'Roštilj', labelEn: 'BBQ', category: 'kitchen' },
  grill: { icon: Flame, labelHr: 'Roštilj', labelEn: 'Grill', category: 'kitchen' },
  grill_indoor: { icon: Flame, labelHr: 'Unutarnji roštilj', labelEn: 'Indoor Grill', category: 'kitchen' },
  ice_maker: { icon: IceCream, labelHr: 'Aparat za led', labelEn: 'Ice Maker', category: 'kitchen' },

  // Bathroom
  bathtub: { icon: Bath, labelHr: 'Kada', labelEn: 'Bathtub', category: 'bathroom' },
  bathtub_jacuzzi: { icon: Bath, labelHr: 'Jacuzzi kada', labelEn: 'Jacuzzi Bathtub', category: 'bathroom' },
  shower: { icon: ShowerHead, labelHr: 'Tuš', labelEn: 'Shower', category: 'bathroom' },
  shower_rain: { icon: ShowerHead, labelHr: 'Rain shower', labelEn: 'Rain Shower', category: 'bathroom' },
  rain_shower: { icon: ShowerHead, labelHr: 'Kišni tuš', labelEn: 'Rain Shower', category: 'bathroom' },
  outdoor_shower: { icon: ShowerHead, labelHr: 'Vanjski tuš', labelEn: 'Outdoor Shower', category: 'bathroom' },
  jacuzzi: { icon: Bath, labelHr: 'Jacuzzi', labelEn: 'Jacuzzi', category: 'bathroom' },
  hot_tub: { icon: Bath, labelHr: 'Masažna kada', labelEn: 'Hot Tub', category: 'bathroom' },
  bidet: { icon: Droplets, labelHr: 'Bide', labelEn: 'Bidet', category: 'bathroom' },
  hair_dryer: { icon: Wind, labelHr: 'Fen za kosu', labelEn: 'Hair Dryer', category: 'bathroom' },
  hairdryer: { icon: Wind, labelHr: 'Fen za kosu', labelEn: 'Hair Dryer', category: 'bathroom' },
  toiletries: { icon: SprayCan, labelHr: 'Toaletne potrepštine', labelEn: 'Toiletries', category: 'bathroom' },
  towels: { icon: Shirt, labelHr: 'Ručnici', labelEn: 'Towels', category: 'bathroom' },
  bathrobes: { icon: Shirt, labelHr: 'Kućni ogrtači', labelEn: 'Bathrobes', category: 'bathroom' },

  // Laundry
  washer: { icon: WashingMachine, labelHr: 'Perilica rublja', labelEn: 'Washer', category: 'laundry' },
  washing_machine: { icon: WashingMachine, labelHr: 'Perilica rublja', labelEn: 'Washing Machine', category: 'laundry' },
  dryer: { icon: Wind, labelHr: 'Sušilica', labelEn: 'Dryer', category: 'laundry' },
  iron: { icon: Shirt, labelHr: 'Glačalo', labelEn: 'Iron', category: 'laundry' },
  ironing_board: { icon: Shirt, labelHr: 'Daska za glačanje', labelEn: 'Ironing Board', category: 'laundry' },
  clothes_rack: { icon: Shirt, labelHr: 'Stalak za sušenje', labelEn: 'Clothes Rack', category: 'laundry' },

  // Outdoor
  garden: { icon: TreePine, labelHr: 'Vrt', labelEn: 'Garden', category: 'outdoor' },
  garden_private: { icon: TreePine, labelHr: 'Privatni vrt', labelEn: 'Private Garden', category: 'outdoor' },
  terrace: { icon: Home, labelHr: 'Terasa', labelEn: 'Terrace', category: 'outdoor' },
  terrace_private: { icon: Home, labelHr: 'Privatna terasa', labelEn: 'Private Terrace', category: 'outdoor' },
  terrace_shared: { icon: Home, labelHr: 'Zajednička terasa', labelEn: 'Shared Terrace', category: 'outdoor' },
  balcony: { icon: Home, labelHr: 'Balkon', labelEn: 'Balcony', category: 'outdoor' },
  patio: { icon: Home, labelHr: 'Trijem', labelEn: 'Patio', category: 'outdoor' },
  private_entrance: { icon: DoorOpen, labelHr: 'Privatni ulaz', labelEn: 'Private Entrance', category: 'outdoor' },
  pool: { icon: Waves, labelHr: 'Bazen', labelEn: 'Pool', category: 'outdoor' },
  pool_heated: { icon: Waves, labelHr: 'Grijani bazen', labelEn: 'Heated Pool', category: 'outdoor' },
  pool_indoor: { icon: Waves, labelHr: 'Unutarnji bazen', labelEn: 'Indoor Pool', category: 'outdoor' },
  pool_children: { icon: Waves, labelHr: 'Dječji bazen', labelEn: 'Children Pool', category: 'outdoor' },
  private_pool: { icon: Waves, labelHr: 'Privatni bazen', labelEn: 'Private Pool', category: 'outdoor' },
  shared_pool: { icon: Waves, labelHr: 'Zajednički bazen', labelEn: 'Shared Pool', category: 'outdoor' },
  heated_pool: { icon: Waves, labelHr: 'Grijani bazen', labelEn: 'Heated Pool', category: 'outdoor' },
  outdoor_furniture: { icon: Armchair, labelHr: 'Vrtni namještaj', labelEn: 'Outdoor Furniture', category: 'outdoor' },
  sun_loungers: { icon: Armchair, labelHr: 'Ležaljke', labelEn: 'Sun Loungers', category: 'outdoor' },
  hammock: { icon: Bed, labelHr: 'Viseća mreža', labelEn: 'Hammock', category: 'outdoor' },
  swing: { icon: Bed, labelHr: 'Ljuljačka', labelEn: 'Swing', category: 'outdoor' },
  outdoor_dining: { icon: UtensilsCrossed, labelHr: 'Vanjska blagovaonica', labelEn: 'Outdoor Dining', category: 'outdoor' },
  playground: { icon: Baby, labelHr: 'Dječje igralište', labelEn: 'Playground', category: 'outdoor' },
  trampoline: { icon: Dumbbell, labelHr: 'Trampolin', labelEn: 'Trampoline', category: 'outdoor' },

  // Sports & Fitness
  gym: { icon: Dumbbell, labelHr: 'Teretana', labelEn: 'Gym', category: 'sports' },
  fitness: { icon: Dumbbell, labelHr: 'Fitness', labelEn: 'Fitness', category: 'sports' },
  sauna: { icon: Thermometer, labelHr: 'Sauna', labelEn: 'Sauna', category: 'sports' },
  steam_room: { icon: Thermometer, labelHr: 'Parna soba', labelEn: 'Steam Room', category: 'sports' },
  spa: { icon: Sparkles, labelHr: 'Spa', labelEn: 'Spa', category: 'sports' },
  massage_chair: { icon: Armchair, labelHr: 'Masažna fotelja', labelEn: 'Massage Chair', category: 'sports' },
  tennis: { icon: Dumbbell, labelHr: 'Tenis', labelEn: 'Tennis Court', category: 'sports' },
  ping_pong: { icon: Dumbbell, labelHr: 'Stolni tenis', labelEn: 'Ping Pong', category: 'sports' },
  basketball: { icon: Dumbbell, labelHr: 'Košarka', labelEn: 'Basketball Court', category: 'sports' },
  basketball_hoop: { icon: Dumbbell, labelHr: 'Košarkaški koš', labelEn: 'Basketball Hoop', category: 'sports' },
  volleyball: { icon: Dumbbell, labelHr: 'Odbojka', labelEn: 'Volleyball', category: 'sports' },
  badminton: { icon: Dumbbell, labelHr: 'Badminton', labelEn: 'Badminton', category: 'sports' },
  bocce: { icon: Dumbbell, labelHr: 'Boćanje', labelEn: 'Bocce', category: 'sports' },
  croquet: { icon: Dumbbell, labelHr: 'Kroket', labelEn: 'Croquet', category: 'sports' },
  mini_golf: { icon: Dumbbell, labelHr: 'Mini golf', labelEn: 'Mini Golf', category: 'sports' },
  yoga_mat: { icon: Dumbbell, labelHr: 'Prostirka za jogu', labelEn: 'Yoga Mat', category: 'sports' },
  bikes: { icon: Bike, labelHr: 'Bicikli', labelEn: 'Bikes', category: 'sports' },
  bicycle: { icon: Bike, labelHr: 'Bicikl', labelEn: 'Bicycle', category: 'sports' },
  bike_storage: { icon: Bike, labelHr: 'Spremište za bicikle', labelEn: 'Bike Storage', category: 'sports' },
  bike_rental: { icon: Bike, labelHr: 'Najam bicikala', labelEn: 'Bike Rental', category: 'sports' },
  kids_bikes: { icon: Bike, labelHr: 'Dječji bicikli', labelEn: 'Kids Bikes', category: 'sports' },

  // Beach & Sea
  beach_access: { icon: Umbrella, labelHr: 'Pristup plaži', labelEn: 'Beach Access', category: 'beach' },
  private_beach: { icon: Umbrella, labelHr: 'Privatna plaža', labelEn: 'Private Beach', category: 'beach' },
  beach_towels: { icon: Shirt, labelHr: 'Ručnici za plažu', labelEn: 'Beach Towels', category: 'beach' },
  beach_chairs: { icon: Armchair, labelHr: 'Stolice za plažu', labelEn: 'Beach Chairs', category: 'beach' },
  beach_umbrella: { icon: Umbrella, labelHr: 'Suncobran', labelEn: 'Beach Umbrella', category: 'beach' },
  beach_equipment: { icon: Umbrella, labelHr: 'Oprema za plažu', labelEn: 'Beach Equipment', category: 'beach' },
  snorkeling: { icon: Waves, labelHr: 'Oprema za ronjenje', labelEn: 'Snorkeling Gear', category: 'beach' },
  snorkeling_gear: { icon: Waves, labelHr: 'Oprema za ronjenje', labelEn: 'Snorkeling Gear', category: 'beach' },
  fishing_gear: { icon: Waves, labelHr: 'Oprema za ribolov', labelEn: 'Fishing Gear', category: 'beach' },
  kayak: { icon: Sailboat, labelHr: 'Kajak', labelEn: 'Kayak', category: 'beach' },
  sup_board: { icon: Sailboat, labelHr: 'SUP daska', labelEn: 'SUP Board', category: 'beach' },
  boat: { icon: Sailboat, labelHr: 'Čamac', labelEn: 'Boat', category: 'beach' },
  jet_ski: { icon: Sailboat, labelHr: 'Jet ski', labelEn: 'Jet Ski', category: 'beach' },

  // Work
  workspace: { icon: Laptop, labelHr: 'Radni prostor', labelEn: 'Workspace', category: 'work' },
  desk: { icon: Laptop, labelHr: 'Radni stol', labelEn: 'Desk', category: 'work' },
  standing_desk: { icon: Laptop, labelHr: 'Stol za rad stojeći', labelEn: 'Standing Desk', category: 'work' },
  office_chair: { icon: Armchair, labelHr: 'Uredska stolica', labelEn: 'Office Chair', category: 'work' },
  ergonomic_chair: { icon: Armchair, labelHr: 'Ergonomska stolica', labelEn: 'Ergonomic Chair', category: 'work' },
  printer: { icon: Printer, labelHr: 'Pisač', labelEn: 'Printer', category: 'work' },
  scanner: { icon: Printer, labelHr: 'Skener', labelEn: 'Scanner', category: 'work' },
  monitor_external: { icon: Monitor, labelHr: 'Vanjski monitor', labelEn: 'External Monitor', category: 'work' },

  // Sleeping
  bed_linen: { icon: BedDouble, labelHr: 'Posteljina', labelEn: 'Bed Linen', category: 'sleeping' },
  linens_premium: { icon: BedDouble, labelHr: 'Premium posteljina', labelEn: 'Premium Linens', category: 'sleeping' },
  bed_king: { icon: BedDouble, labelHr: 'King krevet', labelEn: 'King Bed', category: 'sleeping' },
  bed_queen: { icon: BedDouble, labelHr: 'Queen krevet', labelEn: 'Queen Bed', category: 'sleeping' },
  bed_double: { icon: BedDouble, labelHr: 'Bračni krevet', labelEn: 'Double Bed', category: 'sleeping' },
  bed_single: { icon: Bed, labelHr: 'Jednostruki krevet', labelEn: 'Single Bed', category: 'sleeping' },
  bed_sofa: { icon: Bed, labelHr: 'Kauč na razvlačenje', labelEn: 'Sofa Bed', category: 'sleeping' },
  bed_bunk: { icon: Bed, labelHr: 'Krevet na kat', labelEn: 'Bunk Bed', category: 'sleeping' },
  extra_mattress: { icon: Bed, labelHr: 'Dodatni madrac', labelEn: 'Extra Mattress', category: 'sleeping' },
  extra_pillows: { icon: BedDouble, labelHr: 'Dodatni jastuci', labelEn: 'Extra Pillows', category: 'sleeping' },
  pillows_extra: { icon: BedDouble, labelHr: 'Dodatni jastuci', labelEn: 'Extra Pillows', category: 'sleeping' },
  blackout_curtains: { icon: Moon, labelHr: 'Zamračujuće zavjese', labelEn: 'Blackout Curtains', category: 'sleeping' },
  blackout: { icon: Moon, labelHr: 'Zamračujuće zavjese', labelEn: 'Blackout Curtains', category: 'sleeping' },
  mosquito_net: { icon: Bed, labelHr: 'Mreža protiv komaraca', labelEn: 'Mosquito Net', category: 'sleeping' },
  mosquito_nets: { icon: Bed, labelHr: 'Mreže protiv komaraca', labelEn: 'Mosquito Nets', category: 'sleeping' },
  white_noise: { icon: Moon, labelHr: 'Bijeli šum', labelEn: 'White Noise', category: 'sleeping' },
  soundproofing: { icon: Moon, labelHr: 'Zvučna izolacija', labelEn: 'Soundproofing', category: 'sleeping' },

  // Family
  crib: { icon: Baby, labelHr: 'Dječji krevetić', labelEn: 'Crib', category: 'family' },
  high_chair: { icon: Baby, labelHr: 'Dječja stolica za jelo', labelEn: 'High Chair', category: 'family' },
  baby_bath: { icon: Baby, labelHr: 'Dječja kadica', labelEn: 'Baby Bath', category: 'family' },
  baby_monitor: { icon: Baby, labelHr: 'Baby monitor', labelEn: 'Baby Monitor', category: 'family' },
  changing_table: { icon: Baby, labelHr: 'Previjačka podloga', labelEn: 'Changing Table', category: 'family' },
  bottle_warmer: { icon: Baby, labelHr: 'Grijač za bočice', labelEn: 'Bottle Warmer', category: 'family' },
  stroller: { icon: Baby, labelHr: 'Kolica za dijete', labelEn: 'Stroller', category: 'family' },
  toys: { icon: Baby, labelHr: 'Igračke', labelEn: 'Toys', category: 'family' },
  toys_outdoor: { icon: Baby, labelHr: 'Igračke za dvorište', labelEn: 'Outdoor Toys', category: 'family' },
  toys_beach: { icon: Baby, labelHr: 'Igračke za plažu', labelEn: 'Beach Toys', category: 'family' },
  children_books: { icon: BookOpen, labelHr: 'Dječje knjige', labelEn: "Children's Books", category: 'family' },
  kids_books: { icon: BookOpen, labelHr: 'Dječje knjige', labelEn: "Kids Books", category: 'family' },
  baby_gates: { icon: Baby, labelHr: 'Sigurnosne ogradice', labelEn: 'Baby Gates', category: 'family' },
  outlet_covers: { icon: Baby, labelHr: 'Zaštita za utičnice', labelEn: 'Outlet Covers', category: 'family' },
  corner_guards: { icon: Baby, labelHr: 'Zaštita za kutove', labelEn: 'Corner Guards', category: 'family' },

  // Pets
  pets_allowed: { icon: Dog, labelHr: 'Kućni ljubimci dozvoljeni', labelEn: 'Pets Allowed', category: 'pets' },
  dogs_allowed: { icon: Dog, labelHr: 'Psi dozvoljeni', labelEn: 'Dogs Allowed', category: 'pets' },
  cats_allowed: { icon: Cat, labelHr: 'Mačke dozvoljene', labelEn: 'Cats Allowed', category: 'pets' },
  dog_friendly: { icon: Dog, labelHr: 'Prijateljski prema psima', labelEn: 'Dog Friendly', category: 'pets' },
  cat_friendly: { icon: Cat, labelHr: 'Prijateljski prema mačkama', labelEn: 'Cat Friendly', category: 'pets' },
  pet_bed: { icon: Dog, labelHr: 'Krevet za ljubimca', labelEn: 'Pet Bed', category: 'pets' },
  pet_bowls: { icon: Dog, labelHr: 'Zdjelice za ljubimca', labelEn: 'Pet Bowls', category: 'pets' },
  fenced_yard: { icon: Dog, labelHr: 'Ograđeno dvorište', labelEn: 'Fenced Yard', category: 'pets' },

  // Safety
  smoke_detector: { icon: ShieldCheck, labelHr: 'Detektor dima', labelEn: 'Smoke Detector', category: 'safety' },
  co_detector: { icon: ShieldCheck, labelHr: 'CO detektor', labelEn: 'CO Detector', category: 'safety' },
  carbon_monoxide_detector: { icon: ShieldCheck, labelHr: 'CO detektor', labelEn: 'Carbon Monoxide Detector', category: 'safety' },
  fire_extinguisher: { icon: ShieldCheck, labelHr: 'Aparat za gašenje', labelEn: 'Fire Extinguisher', category: 'safety' },
  first_aid: { icon: Plus, labelHr: 'Prva pomoć', labelEn: 'First Aid', category: 'safety' },
  first_aid_kit: { icon: Plus, labelHr: 'Prva pomoć', labelEn: 'First Aid Kit', category: 'safety' },
  safe: { icon: Lock, labelHr: 'Sef', labelEn: 'Safe', category: 'safety' },
  security_cameras: { icon: Eye, labelHr: 'Sigurnosne kamere', labelEn: 'Security Cameras', category: 'safety' },
  gated: { icon: Lock, labelHr: 'Ograđeno imanje', labelEn: 'Gated Property', category: 'safety' },

  // Accessibility
  wheelchair: { icon: Accessibility, labelHr: 'Pristupačno za invalidska kolica', labelEn: 'Wheelchair Accessible', category: 'accessibility' },
  wheelchair_accessible: { icon: Accessibility, labelHr: 'Pristup za invalidska kolica', labelEn: 'Wheelchair Accessible', category: 'accessibility' },
  step_free_access: { icon: Accessibility, labelHr: 'Pristup bez stepenica', labelEn: 'Step-free Access', category: 'accessibility' },
  wide_doorways: { icon: DoorOpen, labelHr: 'Široka vrata', labelEn: 'Wide Doorways', category: 'accessibility' },
  grab_rails: { icon: Hand, labelHr: 'Rukohvati', labelEn: 'Grab Rails', category: 'accessibility' },

  // Views
  view_sea: { icon: Waves, labelHr: 'Pogled na more', labelEn: 'Sea View', category: 'views' },
  view_mountain: { icon: Mountain, labelHr: 'Pogled na planine', labelEn: 'Mountain View', category: 'views' },
  view_garden: { icon: TreeDeciduous, labelHr: 'Pogled na vrt', labelEn: 'Garden View', category: 'views' },
  view_pool: { icon: Waves, labelHr: 'Pogled na bazen', labelEn: 'Pool View', category: 'views' },
  view_city: { icon: Home, labelHr: 'Pogled na grad', labelEn: 'City View', category: 'views' },
  sea_view: { icon: Waves, labelHr: 'Pogled na more', labelEn: 'Sea View', category: 'views' },
  mountain_view: { icon: Mountain, labelHr: 'Pogled na planine', labelEn: 'Mountain View', category: 'views' },
  garden_view: { icon: TreeDeciduous, labelHr: 'Pogled na vrt', labelEn: 'Garden View', category: 'views' },
  city_view: { icon: Home, labelHr: 'Pogled na grad', labelEn: 'City View', category: 'views' },
  sunrise_view: { icon: Sunrise, labelHr: 'Pogled na izlazak sunca', labelEn: 'Sunrise View', category: 'views' },
  sunset_view: { icon: Sun, labelHr: 'Pogled na zalazak sunca', labelEn: 'Sunset View', category: 'views' },

  // Services
  breakfast: { icon: Coffee, labelHr: 'Doručak dostupan', labelEn: 'Breakfast Available', category: 'services' },
  cleaning: { icon: Sparkles, labelHr: 'Čišćenje', labelEn: 'Cleaning Service', category: 'services' },
  cleaning_daily: { icon: Sparkles, labelHr: 'Dnevno čišćenje', labelEn: 'Daily Cleaning', category: 'services' },
  laundry_service: { icon: WashingMachine, labelHr: 'Usluga pranja', labelEn: 'Laundry Service', category: 'services' },
  airport_shuttle: { icon: CarFront, labelHr: 'Prijevoz do aerodroma', labelEn: 'Airport Shuttle', category: 'services' },
  concierge: { icon: Heart, labelHr: 'Concierge usluga', labelEn: 'Concierge', category: 'services' },
  chef_service: { icon: ChefHat, labelHr: 'Usluga kuhara', labelEn: 'Chef Service', category: 'services' },
  grocery_delivery: { icon: CarFront, labelHr: 'Dostava namirnica', labelEn: 'Grocery Delivery', category: 'services' },
  massage_service: { icon: Heart, labelHr: 'Usluga masaže', labelEn: 'Massage Service', category: 'services' },
  babysitter: { icon: Baby, labelHr: 'Usluga čuvanja djece', labelEn: 'Babysitter', category: 'services' },
  reception: { icon: Heart, labelHr: 'Recepcija 24h', labelEn: '24h Reception', category: 'services' },

  // Basic - additional
  wifi_fast: { icon: Wifi, labelHr: 'Brzi WiFi', labelEn: 'Fast WiFi', category: 'basic' },
  parking_free: { icon: ParkingCircle, labelHr: 'Besplatni parking', labelEn: 'Free Parking', category: 'basic' },
  parking_paid: { icon: ParkingCircle, labelHr: 'Plaćeni parking', labelEn: 'Paid Parking', category: 'basic' },
  parking_private: { icon: ParkingCircle, labelHr: 'Privatni parking', labelEn: 'Private Parking', category: 'basic' },
  parking_garage: { icon: ParkingCircle, labelHr: 'Garaža', labelEn: 'Garage', category: 'basic' },
  ev_charger: { icon: Zap, labelHr: 'Punjač za električna vozila', labelEn: 'EV Charger', category: 'basic' },

  // Entertainment - additional
  hbo: { icon: Monitor, labelHr: 'HBO Max', labelEn: 'HBO Max', category: 'entertainment' },
  disney_plus: { icon: Monitor, labelHr: 'Disney+', labelEn: 'Disney+', category: 'entertainment' },
  amazon_prime: { icon: Monitor, labelHr: 'Amazon Prime', labelEn: 'Amazon Prime', category: 'entertainment' },
  apple_tv: { icon: Monitor, labelHr: 'Apple TV+', labelEn: 'Apple TV+', category: 'entertainment' },
  youtube_premium: { icon: Monitor, labelHr: 'YouTube Premium', labelEn: 'YouTube Premium', category: 'entertainment' },
  speaker_bluetooth: { icon: Speaker, labelHr: 'Bluetooth zvučnik', labelEn: 'Bluetooth Speaker', category: 'entertainment' },
  vinyl_player: { icon: Music, labelHr: 'Gramofon', labelEn: 'Vinyl Player', category: 'entertainment' },
  air_hockey: { icon: Gamepad2, labelHr: 'Zračni hokej', labelEn: 'Air Hockey', category: 'entertainment' },

  // Location / outdoor extras
  beachfront: { icon: Waves, labelHr: 'Uz samu plažu', labelEn: 'Beachfront', category: 'beach' },
  boat_mooring: { icon: Sailboat, labelHr: 'Vez za brod', labelEn: 'Boat Mooring', category: 'beach' },
  courtyard_use: { icon: TreeDeciduous, labelHr: 'Korištenje dvorišta', labelEn: 'Courtyard Use', category: 'outdoor' },
  smoking_allowed: { icon: Cigarette, labelHr: 'Pušenje dozvoljeno', labelEn: 'Smoking Allowed', category: 'outdoor' },
};

export const AmenitiesCard = memo(function AmenitiesCard({ amenities, darkMode, compact = false, notes, hideAmenities }: AmenitiesCardProps) {
  const { t, i18n } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState<Set<CategoryKey>>(new Set());
  const [showAllCompact, setShowAllCompact] = useState(false);

  // Drop ids that a dedicated section already shows in detail (no duplicate).
  if (hideAmenities && hideAmenities.length > 0) {
    const hidden = new Set(hideAmenities);
    amenities = amenities.filter((a) => !hidden.has(a));
  }

  if (!amenities || amenities.length === 0) return null;

  const getAmenityInfo = (amenity: string) => {
    const key = amenity.toLowerCase().replace(/[\s-]/g, '_');
    const config = amenityConfig[key];

    if (config) {
      return {
        Icon: config.icon,
        label: i18n.language === 'hr' ? config.labelHr : config.labelEn,
        category: config.category,
      };
    }

    // Pokušaj naći prikladnu ikonu na temelju ključnih riječi
    const lowerAmenity = amenity.toLowerCase();
    let detectedIcon: React.ElementType = Sparkle;
    let detectedCategory: CategoryKey = 'custom';

    // Kuhinja
    if (lowerAmenity.includes('kuh') || lowerAmenity.includes('cook') || lowerAmenity.includes('kitchen')) {
      detectedIcon = UtensilsCrossed;
      detectedCategory = 'kitchen';
    } else if (lowerAmenity.includes('hladnjak') || lowerAmenity.includes('fridge') || lowerAmenity.includes('refriger')) {
      detectedIcon = Refrigerator;
      detectedCategory = 'kitchen';
    } else if (lowerAmenity.includes('mikro') || lowerAmenity.includes('micro')) {
      detectedIcon = Microwave;
      detectedCategory = 'kitchen';
    } else if (lowerAmenity.includes('kava') || lowerAmenity.includes('coffee') || lowerAmenity.includes('espresso')) {
      detectedIcon = Coffee;
      detectedCategory = 'kitchen';
    } else if (lowerAmenity.includes('roštilj') || lowerAmenity.includes('grill') || lowerAmenity.includes('bbq')) {
      detectedIcon = Flame;
      detectedCategory = 'kitchen';
    } else if (lowerAmenity.includes('pećnica') || lowerAmenity.includes('oven') || lowerAmenity.includes('štednjak') || lowerAmenity.includes('stove')) {
      detectedIcon = Flame;
      detectedCategory = 'kitchen';
    } else if (lowerAmenity.includes('minibar') || lowerAmenity.includes('mini bar') || lowerAmenity.includes('mini-bar')) {
      detectedIcon = Wine;
      detectedCategory = 'kitchen';
    }
    // Kupaonica
    else if (lowerAmenity.includes('tuš') || lowerAmenity.includes('shower')) {
      detectedIcon = ShowerHead;
      detectedCategory = 'bathroom';
    } else if (lowerAmenity.includes('kada') || lowerAmenity.includes('bath') || lowerAmenity.includes('jacuzzi')) {
      detectedIcon = Bath;
      detectedCategory = 'bathroom';
    } else if (lowerAmenity.includes('ručnik') || lowerAmenity.includes('towel')) {
      detectedIcon = Shirt;
      detectedCategory = 'bathroom';
    } else if (lowerAmenity.includes('fen') || lowerAmenity.includes('hair dryer') || lowerAmenity.includes('sušilo')) {
      detectedIcon = Wind;
      detectedCategory = 'bathroom';
    }
    // Klima/grijanje
    else if (lowerAmenity.includes('klima') || lowerAmenity.includes('air con') || lowerAmenity.includes('hlađenje') || lowerAmenity.includes('cooling')) {
      detectedIcon = Snowflake;
      detectedCategory = 'climate';
    } else if (lowerAmenity.includes('grij') || lowerAmenity.includes('heat') || lowerAmenity.includes('toplina')) {
      detectedIcon = Heater;
      detectedCategory = 'climate';
    } else if (lowerAmenity.includes('kamin') || lowerAmenity.includes('fireplace')) {
      detectedIcon = Flame;
      detectedCategory = 'climate';
    } else if (lowerAmenity.includes('ventilator') || lowerAmenity.includes('fan')) {
      detectedIcon = Fan;
      detectedCategory = 'climate';
    }
    // Zabava
    else if (lowerAmenity.includes('tv') || lowerAmenity.includes('televizij') || lowerAmenity.includes('television')) {
      detectedIcon = Tv;
      detectedCategory = 'entertainment';
    } else if (lowerAmenity.includes('playstation') || lowerAmenity.includes('xbox') || lowerAmenity.includes('nintendo') || lowerAmenity.includes('konzola') || lowerAmenity.includes('game') || lowerAmenity.includes('igr')) {
      detectedIcon = Gamepad2;
      detectedCategory = 'entertainment';
    } else if (lowerAmenity.includes('zvučnik') || lowerAmenity.includes('speaker') || lowerAmenity.includes('sound') || lowerAmenity.includes('bluetooth')) {
      detectedIcon = Speaker;
      detectedCategory = 'entertainment';
    } else if (lowerAmenity.includes('knjig') || lowerAmenity.includes('book')) {
      detectedIcon = BookOpen;
      detectedCategory = 'entertainment';
    } else if (lowerAmenity.includes('radio')) {
      detectedIcon = Radio;
      detectedCategory = 'entertainment';
    } else if (lowerAmenity.includes('glazb') || lowerAmenity.includes('music') || lowerAmenity.includes('piano') || lowerAmenity.includes('klavir') || lowerAmenity.includes('gitara') || lowerAmenity.includes('guitar')) {
      detectedIcon = Music;
      detectedCategory = 'entertainment';
    } else if (lowerAmenity.includes('projektor') || lowerAmenity.includes('projector') || lowerAmenity.includes('kino') || lowerAmenity.includes('cinema')) {
      detectedIcon = Monitor;
      detectedCategory = 'entertainment';
    }
    // Vanjski prostor
    else if (lowerAmenity.includes('vrt') || lowerAmenity.includes('garden')) {
      detectedIcon = TreePine;
      detectedCategory = 'outdoor';
    } else if (lowerAmenity.includes('teras') || lowerAmenity.includes('terrace') || lowerAmenity.includes('balkon') || lowerAmenity.includes('balcony')) {
      detectedIcon = Home;
      detectedCategory = 'outdoor';
    } else if (lowerAmenity.includes('bazen') || lowerAmenity.includes('pool')) {
      detectedIcon = Waves;
      detectedCategory = 'outdoor';
    } else if (lowerAmenity.includes('ležaljk') || lowerAmenity.includes('loung') || lowerAmenity.includes('namještaj') || lowerAmenity.includes('furniture')) {
      detectedIcon = Armchair;
      detectedCategory = 'outdoor';
    }
    // Plaža/more
    else if (lowerAmenity.includes('plaž') || lowerAmenity.includes('beach')) {
      detectedIcon = Umbrella;
      detectedCategory = 'beach';
    } else if (lowerAmenity.includes('more') || lowerAmenity.includes('sea') || lowerAmenity.includes('ocean')) {
      detectedIcon = Waves;
      detectedCategory = 'beach';
    } else if (lowerAmenity.includes('kajak') || lowerAmenity.includes('kayak') || lowerAmenity.includes('čamac') || lowerAmenity.includes('boat')) {
      detectedIcon = Sailboat;
      detectedCategory = 'beach';
    } else if (lowerAmenity.includes('suncobran') || lowerAmenity.includes('umbrella')) {
      detectedIcon = Umbrella;
      detectedCategory = 'beach';
    }
    // Sport
    else if (lowerAmenity.includes('teretana') || lowerAmenity.includes('gym') || lowerAmenity.includes('fitness') || lowerAmenity.includes('vježb') || lowerAmenity.includes('exercise')) {
      detectedIcon = Dumbbell;
      detectedCategory = 'sports';
    } else if (lowerAmenity.includes('sauna') || lowerAmenity.includes('spa')) {
      detectedIcon = Thermometer;
      detectedCategory = 'sports';
    } else if (lowerAmenity.includes('bicikl') || lowerAmenity.includes('bike') || lowerAmenity.includes('cycling')) {
      detectedIcon = Bike;
      detectedCategory = 'sports';
    }
    // Rublje
    else if (lowerAmenity.includes('perilica') || lowerAmenity.includes('wash') || lowerAmenity.includes('pranje')) {
      detectedIcon = WashingMachine;
      detectedCategory = 'laundry';
    } else if (lowerAmenity.includes('sušilica') || lowerAmenity.includes('dryer') || lowerAmenity.includes('sušenje')) {
      detectedIcon = Wind;
      detectedCategory = 'laundry';
    } else if (lowerAmenity.includes('glačal') || lowerAmenity.includes('iron')) {
      detectedIcon = Shirt;
      detectedCategory = 'laundry';
    }
    // Rad
    else if (lowerAmenity.includes('radni') || lowerAmenity.includes('work') || lowerAmenity.includes('office') || lowerAmenity.includes('desk') || lowerAmenity.includes('ured')) {
      detectedIcon = Laptop;
      detectedCategory = 'work';
    } else if (lowerAmenity.includes('printer') || lowerAmenity.includes('pisač')) {
      detectedIcon = Printer;
      detectedCategory = 'work';
    }
    // Spavanje
    else if (lowerAmenity.includes('krevet') || lowerAmenity.includes('bed') || lowerAmenity.includes('posteljin') || lowerAmenity.includes('linen')) {
      detectedIcon = BedDouble;
      detectedCategory = 'sleeping';
    } else if (lowerAmenity.includes('jastuk') || lowerAmenity.includes('pillow')) {
      detectedIcon = BedDouble;
      detectedCategory = 'sleeping';
    } else if (lowerAmenity.includes('zavjes') || lowerAmenity.includes('curtain') || lowerAmenity.includes('blackout')) {
      detectedIcon = Moon;
      detectedCategory = 'sleeping';
    } else if (lowerAmenity.includes('izolacij') || lowerAmenity.includes('soundproof') || lowerAmenity.includes('zvučn')) {
      detectedIcon = Moon;
      detectedCategory = 'sleeping';
    }
    // Obitelj
    else if (lowerAmenity.includes('dječj') || lowerAmenity.includes('beb') || lowerAmenity.includes('baby') || lowerAmenity.includes('child') || lowerAmenity.includes('kid') || lowerAmenity.includes('crib')) {
      detectedIcon = Baby;
      detectedCategory = 'family';
    } else if (lowerAmenity.includes('igračk') || lowerAmenity.includes('toy')) {
      detectedIcon = Baby;
      detectedCategory = 'family';
    }
    // Ljubimci
    else if (lowerAmenity.includes('pas') || lowerAmenity.includes('dog') || lowerAmenity.includes('ljubim') || lowerAmenity.includes('pet')) {
      detectedIcon = Dog;
      detectedCategory = 'pets';
    } else if (lowerAmenity.includes('mačk') || lowerAmenity.includes('cat')) {
      detectedIcon = Cat;
      detectedCategory = 'pets';
    }
    // Sigurnost
    else if (lowerAmenity.includes('detektor') || lowerAmenity.includes('detector') || lowerAmenity.includes('alarm') || lowerAmenity.includes('sigurno') || lowerAmenity.includes('security') || lowerAmenity.includes('safe')) {
      detectedIcon = ShieldCheck;
      detectedCategory = 'safety';
    } else if (lowerAmenity.includes('ključ') || lowerAmenity.includes('key') || lowerAmenity.includes('lock') || lowerAmenity.includes('zaključ')) {
      detectedIcon = Key;
      detectedCategory = 'safety';
    } else if (lowerAmenity.includes('kamer') || lowerAmenity.includes('camera')) {
      detectedIcon = Eye;
      detectedCategory = 'safety';
    } else if (lowerAmenity.includes('prva pomoć') || lowerAmenity.includes('first aid')) {
      detectedIcon = Plus;
      detectedCategory = 'safety';
    }
    // Pristupačnost
    else if (lowerAmenity.includes('invalid') || lowerAmenity.includes('wheelchair') || lowerAmenity.includes('pristup') || lowerAmenity.includes('access')) {
      detectedIcon = Accessibility;
      detectedCategory = 'accessibility';
    }
    // Pogled
    else if (lowerAmenity.includes('pogled') || lowerAmenity.includes('view')) {
      if (lowerAmenity.includes('mor') || lowerAmenity.includes('sea')) {
        detectedIcon = Waves;
      } else if (lowerAmenity.includes('planin') || lowerAmenity.includes('mountain')) {
        detectedIcon = Mountain;
      } else if (lowerAmenity.includes('izlazak') || lowerAmenity.includes('sunrise')) {
        detectedIcon = Sunrise;
      } else if (lowerAmenity.includes('zalazak') || lowerAmenity.includes('sunset')) {
        detectedIcon = Sun;
      } else {
        detectedIcon = Eye;
      }
      detectedCategory = 'views';
    }
    // Usluge
    else if (lowerAmenity.includes('doruč') || lowerAmenity.includes('breakfast')) {
      detectedIcon = Coffee;
      detectedCategory = 'services';
    } else if (lowerAmenity.includes('čišćenj') || lowerAmenity.includes('clean')) {
      detectedIcon = Sparkles;
      detectedCategory = 'services';
    } else if (lowerAmenity.includes('prijevoz') || lowerAmenity.includes('transfer') || lowerAmenity.includes('shuttle')) {
      detectedIcon = CarFront;
      detectedCategory = 'services';
    }
    // Osnovne
    else if (lowerAmenity.includes('wifi') || lowerAmenity.includes('internet') || lowerAmenity.includes('net')) {
      detectedIcon = Wifi;
      detectedCategory = 'basic';
    } else if (lowerAmenity.includes('parking') || lowerAmenity.includes('garaž') || lowerAmenity.includes('garage') || lowerAmenity.includes('auto') || lowerAmenity.includes('car')) {
      detectedIcon = Car;
      detectedCategory = 'basic';
    } else if (lowerAmenity.includes('dizalo') || lowerAmenity.includes('elevator') || lowerAmenity.includes('lift')) {
      detectedIcon = DoorOpen;
      detectedCategory = 'basic';
    } else if (lowerAmenity.includes('privatni ulaz') || lowerAmenity.includes('zaseban ulaz') || lowerAmenity.includes('private entrance') || lowerAmenity.includes('separate entrance')) {
      detectedIcon = DoorOpen;
      detectedCategory = 'outdoor';
    }

    return {
      Icon: detectedIcon,
      label: amenity.charAt(0).toUpperCase() + amenity.slice(1).replace(/_/g, ' '),
      category: detectedCategory,
    };
  };

  // Grupiraj amenitije po kategorijama
  const groupedAmenities = amenities.reduce((acc, amenity) => {
    const info = getAmenityInfo(amenity);
    if (!acc[info.category]) {
      acc[info.category] = [];
    }
    acc[info.category].push({ amenity, ...info });
    return acc;
  }, {} as Record<CategoryKey, Array<{ amenity: string; Icon: React.ElementType; label: string; category: CategoryKey }>>);

  const toggleCategory = (category: CategoryKey) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Sortiraj kategorije po broju amenitija
  const sortedCategories = Object.entries(groupedAmenities)
    .sort(([, a], [, b]) => b.length - a.length) as [CategoryKey, typeof groupedAmenities[CategoryKey]][];

  // Host notes for amenities that have one (shown as a small list under the pills).
  const noteItems = amenities
    .filter((a) => notes?.[a]?.trim())
    .map((a) => ({ label: getAmenityInfo(a).label, note: notes![a] }));

  const renderNotes = () =>
    noteItems.length > 0 && (
      <div className={`mt-4 space-y-1.5 rounded-xl p-3 ${darkMode ? 'bg-slate-700/40' : 'bg-emerald-50/60'}`}>
        <p className={`text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
          {t('guest.amenities.notes', 'Napomene')}
        </p>
        {noteItems.map((n, i) => (
          <p key={i} className={`text-xs ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
            <span className="font-medium">{n.label}:</span> {n.note}
          </p>
        ))}
      </div>
    );

  // Compact mode: flat chip/pill layout
  if (compact) {
    const allItems = sortedCategories.flatMap(([, items]) => items);
    const COMPACT_LIMIT = 10;
    const displayItems = showAllCompact ? allItems : allItems.slice(0, COMPACT_LIMIT);
    const hasMore = allItems.length > COMPACT_LIMIT;

    return (
      <div
        className={`rounded-2xl overflow-hidden transition-all ${
          darkMode
            ? 'bg-slate-800/50 border border-slate-700/50'
            : 'bg-gradient-to-br from-white to-emerald-50/30 border border-emerald-100/40 shadow-lg shadow-gray-200/80'
        }`}
      >
        <div className="p-5">
          {/* Header — strong text title (no icon badge; group icon is above). */}
          <div className={`flex items-center gap-3 mb-4 pb-3 border-b ${darkMode ? 'border-slate-700' : 'border-gray-100'}`}>
            <div>
              <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('guest.amenities.title', 'Sadržaji')}
              </h3>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                {amenities.length} {t('guest.amenities.available', 'dostupno')}
              </p>
            </div>
          </div>

          {/* Chip/Pill grid */}
          <div className="flex flex-wrap gap-2">
            {displayItems.map((item, index) => {
              const catConfig = categoryConfig[item.category];
              return (
                <div
                  key={index}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    darkMode
                      ? `${catConfig.darkBgClass} ${catConfig.darkColorClass}`
                      : `${catConfig.bgClass} ${catConfig.colorClass}`
                  }`}
                >
                  <item.Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>

          {/* Show more/less */}
          {hasMore && (
            <button
              onClick={() => setShowAllCompact(!showAllCompact)}
              className={`mt-3 flex items-center gap-1 text-xs font-medium transition-colors ${
                darkMode
                  ? 'text-emerald-400 hover:text-emerald-300'
                  : 'text-emerald-600 hover:text-emerald-700'
              }`}
            >
              <span>
                {showAllCompact
                  ? t('amenities.showLess', 'Show less')
                  : t('amenities.showAllCount', 'Show all ({{count}})', { count: allItems.length })
                }
              </span>
              {showAllCompact ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
            </button>
          )}

          {renderNotes()}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl overflow-hidden transition-all ${
        darkMode
          ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-white border border-emerald-100/40 shadow-lg shadow-gray-200/80'
      }`}
    >
      <div className="relative p-5">
        {/* Header — strong text title (no icon badge; group icon is above). */}
        <div className={`flex items-center gap-3 mb-5 pb-3 border-b ${darkMode ? 'border-slate-700' : 'border-gray-100'}`}>
          <div>
            <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('guest.amenities.title', 'Sadržaji')}
            </h3>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {amenities.length} {t('guest.amenities.available', 'dostupno')}
            </p>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-3">
          {sortedCategories.map(([category, items]) => {
            const catConfig = categoryConfig[category];
            const CatIcon = catConfig.icon;
            const isExpanded = expandedCategories.has(category);
            const showExpand = items.length > 6;
            const displayItems = showExpand && !isExpanded ? items.slice(0, 6) : items;

            return (
              <div
                key={category}
                className={`rounded-xl overflow-hidden ${
                  darkMode
                    ? 'bg-slate-700/30'
                    : 'bg-white shadow-sm'
                }`}
              >
                {/* Category Header */}
                <div className={`flex items-center gap-2 px-3 py-2 ${
                  darkMode ? 'bg-slate-700/50' : 'bg-gray-50'
                }`}>
                  <div className={`p-1.5 rounded-lg ${darkMode ? catConfig.darkBgClass : catConfig.bgClass}`}>
                    <CatIcon className={`w-4 h-4 ${darkMode ? catConfig.darkColorClass : catConfig.colorClass}`} />
                  </div>
                  <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {i18n.language === 'hr' ? catConfig.labelHr : catConfig.labelEn}
                  </span>
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    ({items.length})
                  </span>
                </div>

                {/* Amenities Grid */}
                <div className="p-2">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    {displayItems.map((item, index) => (
                      <div
                        key={index}
                        className={`flex flex-col items-center p-2 rounded-lg transition-all ${
                          darkMode
                            ? 'bg-slate-700/50 hover:bg-slate-600/50'
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className={`p-1.5 rounded-lg mb-1 ${darkMode ? catConfig.darkBgClass : catConfig.bgClass}`}>
                          <item.Icon className={`w-4 h-4 ${darkMode ? catConfig.darkColorClass : catConfig.colorClass}`} />
                        </div>
                        <span className={`text-[10px] text-center font-medium leading-tight ${
                          darkMode ? 'text-slate-300' : 'text-gray-700'
                        }`}>
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Expand/Collapse Button */}
                  {showExpand && (
                    <button
                      onClick={() => toggleCategory(category)}
                      className={`w-full mt-2 py-1.5 flex items-center justify-center gap-1 rounded-lg transition-all ${
                        darkMode
                          ? 'bg-slate-700/50 hover:bg-slate-600/50 text-slate-400'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                      }`}
                    >
                      <span className="text-xs font-medium">
                        {isExpanded
                          ? t('amenities.showLess', 'Show less')
                          : t('amenities.moreCount', '{{count}} more', { count: items.length - 6 })
                        }
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {renderNotes()}
      </div>
    </div>
  );
});

export default AmenitiesCard;
